<template>
    <div class="uc-wrap">
        <div class="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
                <h6 class="font-semibold mb-1">Utilisateurs connectés</h6>
                <p class="text-[0.8rem] text-defaulttextcolor/70 mb-0">
                    Utilisateurs  ayant une session active en ce moment.
                    Vous pouvez déconnecter un poste (utile pour libérer un utilisateur bloqué).
                </p>
            </div>
            <div class="flex items-center gap-2">
                <span class="badge bg-primary/10 text-primary text-[0.8rem]">
                    {{ used }} connecté(s)
                </span>
                <button class="ti-btn ti-btn-primary-full ti-btn-sm" :disabled="loading" @click="charger">
                    <i class="ri-refresh-line" :class="{ 'animate-spin': loading }"></i>
                    <span>Actualiser</span>
                </button>
            </div>
        </div>

        <div v-if="loading && !loaded" class="text-center py-6 text-defaulttextcolor/60">
            <i class="ri-loader-4-line animate-spin text-2xl"></i>
            <p class="mb-0 mt-2">Chargement…</p>
        </div>

        <div v-else-if="erreur" class="alert alert-danger" role="alert">
            <i class="ri-error-warning-line me-1"></i> {{ erreur }}
        </div>

        <div v-else-if="loaded && !users.length" class="text-center py-6 text-defaulttextcolor/60">
            <i class="ri-user-line text-2xl"></i>
            <p class="mb-0 mt-2">Aucun utilisateur connecté pour le moment.</p>
        </div>

        <div v-else-if="loaded" class="table-responsive">
            <table class="table table-hover whitespace-nowrap min-w-full">
                <thead>
                    <tr>
                        <th class="text-start">Utilisateur</th>
                        <th class="text-start">Statut</th>
                        <th class="text-start">Connecté depuis</th>
                        <th class="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(u, i) in users" :key="i">
                        <td>
                            <span class="font-semibold">{{ u.username || '-' }}</span>
                        </td>
                        <td>
                            <span class="badge bg-success/10 text-success">
                                <i class="ri-circle-fill text-[0.5rem] me-1"></i>
                                En ligne
                            </span>
                        </td>
                        <td class="text-[0.8rem]">{{ formatDateTime(u.connecte_at) }}</td>
                        <td class="text-end">
                            <span v-if="u.is_moi" class="badge bg-primary/10 text-primary">Vous</span>
                            <button v-else
                                class="ti-btn ti-btn-danger-full ti-btn-sm"
                                :disabled="deconnexionloading"
                                @click="askDeconnexion(u)">
                                <i class="ri-logout-box-r-line me-1"></i>
                                <span>Déconnecter</span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- ===== Modale de confirmation de déconnexion ===== -->
        <div v-if="confirmOpen" class="uc-modal-overlay" @click.self="fermerModale">
            <div class="uc-modal">
                <div class="flex items-start gap-3 mb-3">
                    <i class="ri-error-warning-line text-2xl text-danger"></i>
                    <div>
                        <h6 class="font-semibold mb-1">Déconnecter cet utilisateur ?</h6>
                        <p class="text-[0.85rem] text-defaulttextcolor/70 mb-0">
                            <strong>{{ cible && cible.username }}</strong> sera déconnecté de son poste.
                            Toute saisie non enregistrée de sa part peut être perdue.
                        </p>
                    </div>
                </div>

                <div v-if="modalerreur" class="alert alert-danger py-2 text-[0.8rem]" role="alert">
                    {{ modalerreur }}
                </div>

                <div class="flex items-center justify-end gap-2 mt-4">
                    <button class="ti-btn ti-btn-light ti-btn-sm" :disabled="deconnexionloading" @click="fermerModale">
                        Annuler
                    </button>
                    <button class="ti-btn ti-btn-danger-full ti-btn-sm" :disabled="deconnexionloading" @click="confirmDeconnexion">
                        <i v-if="deconnexionloading" class="ri-loader-4-line animate-spin me-1"></i>
                        <span>Déconnecter</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    const axios = require('axios');
    import * as JwtService from "@/common/jwt.service";
    import { randomString } from "@/allfunction";

    /**
     * Onglet Paramètres - utilisateurs actuellement connectés + déconnexion.
     *
     * Lecture : /setting/utilisateur/:keyuser/connectes (société lue dans la session côté back).
     * Déconnexion : POST /setting/utilisateur/:keyuser/deconnecter avec { cible: keyuser }.
     * Le back scope tout par la société de l'admin et refuse l'auto-déconnexion (is_moi).
     */
    export default {
        name: "utilisateursconnectes",
        data: () => ({
            loading: false,
            loaded: false,
            erreur: "",
            users: [],
            used: 0,
            // Modale de confirmation
            confirmOpen: false,
            cible: null,
            deconnexionloading: false,
            modalerreur: "",
        }),
        methods: {
            _headers() {
                return {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + JwtService.getToken(),
                    "csrftoken": this.$store.state.appsociete,
                };
            },

            formatDateTime(v) {
                if (!v) return "-";
                const d = new Date(v);
                if (isNaN(d.getTime())) return String(v);
                const p = (n) => String(n).padStart(2, "0");
                return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
            },

            async charger() {
                this.loading = true;
                this.erreur = "";
                const url = process.env.VUE_APP_API_URL
                    + "/setting/utilisateur/" + this.$store.state.appsociete
                    + "/connectes/" + randomString();
                try {
                    const response = await axios.get(url, { headers: this._headers(), withCredentials: true });

                    const d = response.data || {};
                    if (d.success && d.m) {
                        this.users = Array.isArray(d.m.users) ? d.m.users : [];
                        this.used = Number(d.m.used) || this.users.length;
                    } else {
                        this.erreur = "Accès refusé ou liste indisponible.";
                    }
                    this.loaded = true;
                } catch (e) {
                    console.error("[utilisateursconnectes] chargement:", e);
                    this.erreur = "Impossible de charger la liste des utilisateurs connectés.";
                    this.loaded = true;
                } finally {
                    this.loading = false;
                }
            },

            askDeconnexion(u) {
                this.cible = u;
                this.modalerreur = "";
                this.confirmOpen = true;
            },

            fermerModale() {
                if (this.deconnexionloading) return;
                this.confirmOpen = false;
                this.cible = null;
                this.modalerreur = "";
            },

            async confirmDeconnexion() {
                if (!this.cible) return;
                this.deconnexionloading = true;
                this.modalerreur = "";
                const url = process.env.VUE_APP_API_URL
                    + "/setting/utilisateur/" + this.$store.state.appsociete
                    + "/deconnecter/" + randomString();
                try {
                    const response = await axios.post(url, { cible: this.cible.keyuser },
                        { headers: this._headers(), withCredentials: true });

                    const d = response.data || {};
                    if (d.success) {
                        this.confirmOpen = false;
                        this.cible = null;
                        await this.charger();
                    } else {
                        this.modalerreur = d.message || "Échec de la déconnexion.";
                    }
                } catch (e) {
                    console.error("[utilisateursconnectes] déconnexion:", e);
                    this.modalerreur = "Impossible de déconnecter cet utilisateur.";
                } finally {
                    this.deconnexionloading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .uc-wrap { padding: 0.25rem; }
    .uc-modal-overlay {
        position: fixed; inset: 0; z-index: 1055;
        background: rgba(0, 0, 0, 0.45);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem;
    }
    .uc-modal {
        background: rgb(var(--custom-white, 255 255 255));
        color: var(--defaulttextcolor);
        width: 100%; max-width: 420px;
        border-radius: 0.5rem;
        padding: 1.25rem;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    }
</style>
